import React, { useEffect, useMemo, useState } from 'react';
import { isValidMove } from '../utils/deck';
import WhotCard from './WhotCard';
import '../../styles/Whot.css';
import '../../styles/Flappy.css';

export default function WhotPlayerHand({
  cards,
  topCard,
  currentShape,
  isCurrentPlayer,
  pendingPick2Active = false,
  pendingPick3Active = false,
  disablePlayableHighlight = false,
  playerName,
  onPlayCard,
  isOpponent = false,
  onRegisterCardEl,
  hiddenCardIds,
  disabled = false,
  containerClassName = '',
  enableSwapSorting = true,
  rating = null,
  streak = 0,
}) {
  const hiddenSet = useMemo(() => new Set(hiddenCardIds || []), [hiddenCardIds]);
  const [handOrder, setHandOrder] = useState([]);
  const [draggedCardId, setDraggedCardId] = useState(null);

  const playableCards = useMemo(() => {
    if (!isCurrentPlayer || isOpponent) return [];
    if (pendingPick2Active) return [];
    if (pendingPick3Active) {
      return (cards || []).filter((c) => c.number === 5);
    }
    return (cards || []).filter((c) => isValidMove(c, topCard, currentShape) || c.number === 20);
  }, [cards, topCard, currentShape, isCurrentPlayer, isOpponent, pendingPick2Active, pendingPick3Active]);

  const playableIds = useMemo(() => new Set(playableCards.map((c) => c.id)), [playableCards]);

  useEffect(() => {
    const ids = (cards || []).map((card) => card.id);
    setHandOrder((prev) => {
      if (!prev.length) return ids;
      const stillPresent = prev.filter((id) => ids.includes(id));
      const added = ids.filter((id) => !stillPresent.includes(id));
      return [...stillPresent, ...added];
    });
  }, [cards]);

  const orderedCards = useMemo(() => {
    const byId = new Map((cards || []).map((card) => [card.id, card]));
    return handOrder.map((id) => byId.get(id)).filter(Boolean);
  }, [cards, handOrder]);

  const swapCards = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setHandOrder((prev) => {
      const sourceIdx = prev.indexOf(sourceId);
      const targetIdx = prev.indexOf(targetId);
      if (sourceIdx < 0 || targetIdx < 0) return prev;
      const next = [...prev];
      next[sourceIdx] = targetId;
      next[targetIdx] = sourceId;
      return next;
    });
  };

  const onClickCard = (card) => {
    if (!isCurrentPlayer || isOpponent || disabled) return;
    if (!playableIds.has(card.id)) return;
    onPlayCard?.(card, { sourceCardId: card.id });
  };

  if (isOpponent) {
    const count = (cards || []).length;
    const displayCount = Math.min(count || 0, 4);
    const anglesForCount = {
      1: [0],
      2: [-5, 5],
      3: [-8, 0, 8],
      4: [-12, -4, 4, 12],
    };
    const angles = anglesForCount[displayCount] || [];

    return (
      <div className={containerClassName}>
        <div className="whot-opponent-head" style={{ marginBottom: 0, position: 'relative', top: '16px', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          {!playerName.startsWith('Bot') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <strong>{playerName}</strong>
              
              {Number(streak) >= 3 && <span style={{ fontSize: 11 }}>🔥 {streak}</span>}
            </div>
          )}
        </div>

        <div className="whot-opponent-fan" aria-label={`${playerName} has ${count} cards`}>
          {Array.from({ length: displayCount }).map((_, i) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className="whot-opponent-fan-card"
              style={{
                left: `${i * 10}px`,
                transform: `rotate(${angles[i] ?? 0}deg)`,
              }}
            >
              <WhotCard isFaceDown isSmall badge={i === displayCount - 1 ? count : null} onClick={() => {}} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '0', marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="whot-muted" style={{ fontWeight: 900, fontSize: '1em', textAlign: 'center' }}>
            {playerName}: {(cards || []).length} cards
          </div>
          
          {Number(streak) >= 3 && <span style={{ fontSize: 12 }}>🔥 {streak}</span>}
        </div>
      </div>

      <div className={["whot-hand", containerClassName].filter(Boolean).join(' ')} style={{ margin: 0, padding: '0.3rem 0 0.5rem 0', borderRadius: 0, background: 'none', boxShadow: 'none', minHeight: 0, justifyContent: 'center', display: 'flex', paddingLeft: '16px', paddingRight: '16px' }}>
        {(orderedCards || []).map((card, index) => {
          const isPlayable = playableIds.has(card.id);
          const isHidden = hiddenSet.has(card.id);
          const canPlayCard = isCurrentPlayer ? isPlayable && !disabled : false;
          const canDragCard = enableSwapSorting && !isOpponent && !isHidden;
          return (
            <div
              key={card.id}
              className={`whot-hand-card ${draggedCardId === card.id ? 'whot-hand-card--dragging' : ''}`}
              ref={(el) => onRegisterCardEl?.(card.id, el)}
              draggable={canDragCard}
              onDragStart={() => {
                if (!canDragCard) return;
                setDraggedCardId(card.id);
              }}
              onDragEnd={() => setDraggedCardId(null)}
              onDragOver={(event) => {
                if (!canDragCard || !draggedCardId || draggedCardId === card.id) return;
                event.preventDefault();
              }}
              onDrop={(event) => {
                if (!canDragCard || !draggedCardId || draggedCardId === card.id) return;
                event.preventDefault();
                swapCards(draggedCardId, card.id);
                setDraggedCardId(null);
              }}
              style={{
                opacity: isHidden ? 0 : 1,
                pointerEvents: isHidden ? 'none' : 'auto',
                marginLeft: index > 0 ? '-32px' : '0',
                transition: 'margin 0.2s ease',
              }}
              title={canDragCard ? 'Drag to swap card position' : undefined}
            >
              <WhotCard
                card={card}
                isSelected={false}
                isPlayable={canPlayCard}
                isSmall={true}
                onClick={onClickCard}
                suppressPlayableStyles={disablePlayableHighlight}
              />
            </div>
          );
        })}
      </div>
    </>
  );
}

